
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout/Layout";
import { GroupHeader } from "@/components/Groups/GroupHeader";
import { GroupDetailActions } from "@/components/Groups/GroupDetailActions";
import { GroupTabs } from "@/components/Groups/GroupTabs";
import { GroupBannedView } from "@/components/Groups/GroupBannedView";
import { GroupJoinPrompt } from "@/components/Groups/GroupJoinPrompt";
import { CreateChannelDialog } from "@/components/Groups/CreateChannelDialog";
import { GroupSettingsDialog } from "@/components/Groups/GroupSettingsDialog";
import { useGroupDetails } from "@/hooks/use-group-details";
import { GroupDetailsSkeleton } from "@/components/Groups/GroupDetailsSkeleton";

const GroupDetails = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [showCreateChannelDialog, setShowCreateChannelDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  const {
    group,
    channels,
    memberCount,
    currentMember,
    loading,
    handleJoinGroup,
    handleLeaveGroup,
    handleCreateChannel,
    handleUpdateGroup,
    handleDeleteGroup,
    handleShareGroup
  } = useGroupDetails(groupId, user?.id);

  if (loading) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8">
          <GroupDetailsSkeleton />
        </div>
      </Layout>
    );
  }

  if (!group) {
    navigate("/groups");
    return null;
  }

  if (currentMember?.isBanned) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8">
          <GroupBannedView groupName={group.name} />
        </div>
      </Layout>
    );
  }
  
  const onCreateChannel = async (channelData: { 
    name: string; 
    description: string; 
    isAnnouncement: boolean; 
    isTemporary: boolean; 
    autoDeleteAfter?: number 
  }) => {
    const success = await handleCreateChannel(channelData);
    if (success) {
      setShowCreateChannelDialog(false);
    }
  };
  
  const onUpdateGroup = async (updatedGroup: { 
    name: string; 
    description?: string; 
    rules?: string 
  }) => {
    const success = await handleUpdateGroup(updatedGroup);
    if (success) {
      setShowSettingsDialog(false);
    }
  };

  const onDeleteGroup = async () => {
    const success = await handleDeleteGroup();
    if (success) {
      navigate("/groups");
    }
  };

  const handleJoinGroupWrapper = async () => {
    await handleJoinGroup();
  };

  const handleLeaveGroupWrapper = async () => {
    await handleLeaveGroup();
  };

  const handleShareGroupWrapper = () => {
    handleShareGroup(group);
  };

  return (
    <Layout>
      <div className="container max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-8">
          <GroupHeader group={group} memberCount={memberCount} />
          
          <GroupDetailActions 
            currentMember={currentMember}
            group={group}
            onJoinGroup={handleJoinGroupWrapper}
            onLeaveGroup={handleLeaveGroupWrapper}
            onOpenSettings={() => setShowSettingsDialog(true)}
            onOpenCreateChannel={() => setShowCreateChannelDialog(true)}
            onShareGroup={handleShareGroupWrapper}
          />
        </div>

        {currentMember ? (
          <GroupTabs
            groupId={groupId || ""}
            currentUserRole={currentMember.role}
            currentUserId={user?.id || ""}
          />
        ) : (
          <GroupJoinPrompt 
            group={group} 
            onJoinGroup={handleJoinGroupWrapper} 
          />
        )}
      </div>

      {showCreateChannelDialog && (
        <CreateChannelDialog 
          open={showCreateChannelDialog} 
          onOpenChange={setShowCreateChannelDialog}
          onCreateChannel={onCreateChannel}
        />
      )}

      {showSettingsDialog && group && (
        <GroupSettingsDialog 
          open={showSettingsDialog} 
          onOpenChange={setShowSettingsDialog}
          group={group}
          onUpdateGroup={onUpdateGroup}
          onDeleteGroup={onDeleteGroup}
        />
      )}
    </Layout>
  );
};

export default GroupDetails;
